import { useApolloClient } from "@apollo/client";
import { assertNever, getDataOrNull } from "@msys/common";
import {
  CollapseSection,
  LabeledValue,
  LabeledValueWithButton,
  MenuButton,
  Modal,
  ModalOpenButton,
  Select,
  useLocalStorageAsState,
  useScreenWidth,
} from "@msys/ui";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Functions as FunctionsIcon,
} from "@mui/icons-material";
import { Grid, IconButton, MenuItem, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { differenceWith, keyBy, noop, uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import {
  type DocType,
  EntitySearchArrayOfFilterOperator,
  EntitySearchBoolFilterOperator,
  EntitySearchBoolInFilterOperator,
  EntitySearchNumberBetweenFilterOperator,
  EntitySearchNumberFilterOperator,
  EntitySearchNumberInFilterOperator,
  EntitySearchTextFilterOperator,
  EntitySearchTextInFilterOperator,
  ItemType,
  ModifyItemRecommendedTemplateTypeValuesInput,
  PermissionName,
} from "../../../clients/graphqlTypes.js";
import { RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug } from "../../auth/RestrictedByCapabilityAndDocumentPermissionByType.js";
import type { DocumentByType } from "../../auth/RestrictedByDocumentPermissionByType.js";
import { FullScreenToggleIconButton } from "../../commons/button/FullScreenToggleIconButton.js";
import {
  ConfirmModalProps,
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../commons/modals/ConfirmProcess.js";
import { ViewMode, ViewModeMenuItem } from "../../commons/ViewModeMenuItem.js";
import type { TemplateSearchSortingDefinitionFragment } from "../doc-items/boxes/TemplateTypePlaceholderBox.generated.js";
import { SearchFilterField } from "../doc-items/SearchFilterField.js";
import {
  authoredFilterFragment,
  isComputedSearchFilterSlot,
  newAuthoredFilterForProp,
  searchFilterFieldPropsForSlot,
  searchFilterOperator,
  searchFilterOperatorOptions,
  searchFilterSlotToInput,
  searchFilterValueOptionsFromProps2,
  searchFilterValueText,
  withExpressionFragment,
  withOperatorFragment,
  withoutExpressionFragment,
} from "../doc-items/searchFilterSlots.js";
import { SearchFilterValueInput } from "../doc-items/SearchFilterValueInput.js";
import {
  SearchFilterOperatorsModal,
  type SearchFilterOperatorRow,
} from "../doc-items/modals/SearchFilterOperatorsModal.js";
import {
  ExpressionInputWithVariables,
  type VariableInputState,
  type ExpressionItemSource,
} from "../doc-items/modals/ExpressionInputWithVariables.js";
import { useExpressionValidation } from "../doc-items/modals/useExpressionValidation.js";
import {
  getEnhancedPropertyLabel,
  getEnhancedTemplateSearchFilterPropLabel,
  propToTemplateSearchFilterProp,
} from "../doc-items/properties.js";
import { templateSearchSortingPropertySortingDefinition2Input } from "../doc-items/useTemplateSearchSortingsMutations.js";
import {
  ArrayFilterOperatorOptions,
  BoolFilterOperatorOptions,
  NumberFilterOperatorOptions,
  TextFilterOperatorOptions,
} from "../products/ProductSearchFilterOperatorOptions.js";
import {
  EditRecommendedTemplateTypeModal_ItemRecommendationConfigFragment,
  EditRecommendedTemplateTypeModal_ItemRecommendationFragment,
  EditRecommendedTemplateTypeModal_RecommendedTemplateTypeConfigFragment,
  useEditRecommendedTemplateTypeModalQuery,
} from "./EditRecommendedTemplateTypeModal.generated.js";
import type { ItemTemplateSearchFilterPropertyFilterFragment } from "./templateSearchFilters.generated.js";

const EXPRESSION_SOURCES: ExpressionItemSource[] = ["self"];

interface FormValues {
  includeIfExpr: VariableInputState;
  eligibleIfExpr: VariableInputState;
  templateSearchFilterDefinitions: {
    propertyFilters: ItemTemplateSearchFilterPropertyFilterFragment[];
  };
  templateSearchSortingDefinitions: TemplateSearchSortingDefinitionFragment[];
}

interface Props {
  docId: string;
  docType: DocType;
  itemId: string;
  templateTypeId: string;
  recommendation: EditRecommendedTemplateTypeModal_ItemRecommendationFragment;
  recommendationConfig: EditRecommendedTemplateTypeModal_ItemRecommendationConfigFragment;
  recommendedTemplateTypeConfig: EditRecommendedTemplateTypeModal_RecommendedTemplateTypeConfigFragment;
  allowedItemTypes: ItemType[];
  handleClose(): void;
  handleComplete(
    values: ModifyItemRecommendedTemplateTypeValuesInput | null
  ): Promise<void> | void;
  documentViewerPermissions: PermissionName[];
}

export const EditRecommendedTemplateTypeModal = ({
  docId,
  itemId,
  templateTypeId,
  recommendation,
  recommendationConfig,
  recommendedTemplateTypeConfig,
  allowedItemTypes,
  handleClose,
  handleComplete,
  docType,
  documentViewerPermissions,
}: Props) => {
  const { t } = useTranslate(["QuoteItem", "TemplateTypes", "Global"]);

  const document: DocumentByType = {
    docType,
    viewerPermissions: documentViewerPermissions,
  };

  const renderExpressionGate = (
    gated: React.ReactNode,
    fallback: React.ReactNode
  ) => (
    <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
      capability="expressions"
      permissionByType={{
        QUOTE: "quote_expressions_update",
        REQUIREMENT: "requirement_expressions_update",
        TEMPLATE: "template_expressions_update",
        BUILDING: "building_expressions_update",
      }}
      document={document}
      otherwise={fallback}
    >
      {gated}
    </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
  );

  const { isMaxPhone } = useScreenWidth();
  const [isFullScreen, setIsFullScreen] = useLocalStorageAsState<boolean>(
    "msys-recommended-template-type-modal-fullscreen",
    false,
    true
  );

  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();
  const query = useEditRecommendedTemplateTypeModalQuery({
    client,
    variables: { templateTypeId },
  });
  const templateType = getDataOrNull(query.data?.templateType);

  const [isDeleting, setIsDeleting] = React.useState<boolean>(false);

  const [viewModePropertyExpression, setViewModePropertyExpression] =
    React.useState<ViewMode>(null);
  const [viewModeSearchSortings, setViewModeSearchSortings] =
    React.useState<ViewMode>(null);

  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);
  const startConfirmProcess = React.useCallback((props: ConfirmModalProps) => {
    return confirmProcessRef.current!.startConfirmProcess(props);
  }, []);

  const { expressionCheckResults, validateExpressions } =
    useExpressionValidation(docId, itemId);
  const eligibleIfExprError = expressionCheckResults?.find(
    value =>
      value.__typename ===
        "CompileDocIsolatedExpressionResultDiagnosticRecommendedEligibleIf" &&
      value.itemRecommendationId === recommendation.id
  );
  const includeIfExprError = expressionCheckResults?.find(
    value =>
      value.__typename ===
        "CompileDocIsolatedExpressionResultDiagnosticRecommendedIncludeIf" &&
      value.itemRecommendationId === recommendation.id
  );

  const initialValues: FormValues = {
    includeIfExpr: {
      expression: recommendationConfig.includeIfExpr,
      cursorIndex: null,
    },
    eligibleIfExpr: {
      expression: recommendationConfig.eligibleIfExpr,
      cursorIndex: null,
    },
    templateSearchFilterDefinitions:
      recommendedTemplateTypeConfig.templateSearchFilterDefinitions,
    templateSearchSortingDefinitions:
      recommendedTemplateTypeConfig.templateSearchSortingDefinitions,
  };

  const formId = React.useMemo(() => uniqueId(), []);

  const validationSchema = Yup.object().shape({
    includeIfExpr: Yup.object().required().shape({
      expression: Yup.string(),
      cursorIndex: Yup.number().nullable(),
    }),
    eligibleIfExpr: Yup.object().required().shape({
      expression: Yup.string(),
      cursorIndex: Yup.number().nullable(),
    }),
    templateSearchFilterDefinitions: Yup.mixed().required(),
  });

  return (
    <Formik<FormValues>
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async values => {
        const result = await validateExpressions({
          docId,
          itemId,
          overrides: [
            {
              itemId,
              recommended: {
                eligibleIf: values.eligibleIfExpr.expression,
                includeIf: values.includeIfExpr.expression,
                recommendationId: recommendation.id,
              },
            },
          ],
        });

        const expressionCheckResult = getDataOrNull(
          result.data?.compileDocIsolatedExpression
        )?.results?.filter(
          result =>
            (result.__typename ===
              "CompileDocIsolatedExpressionResultDiagnosticRecommendedEligibleIf" ||
              result.__typename ===
                "CompileDocIsolatedExpressionResultDiagnosticRecommendedIncludeIf") &&
            result.itemRecommendationId === recommendation.id
        );

        if (!expressionCheckResult) {
          console.error("compile document error");
          enqueueSnackbar(`Cannot compile expressions! Not allowed`, {
            variant: "error",
          });
          return;
        }
        if (expressionCheckResult.length > 0) {
          console.error("compile document error", expressionCheckResult);
          expressionCheckResult.forEach(result => {
            enqueueSnackbar(`Expression is invalid: ${result.messageText}`, {
              variant: "error",
            });
          });
          return;
        }

        await handleComplete({
          includeIfExpr: values.includeIfExpr.expression,
          eligibleIfExpr: values.eligibleIfExpr.expression,
          templateSearchFilterDefinitions: {
            propertyFilters:
              values.templateSearchFilterDefinitions.propertyFilters.map(
                searchFilterSlotToInput
              ),
          },
          templateSearchSortingDefinitions:
            values.templateSearchSortingDefinitions.map(
              templateSearchSortingPropertySortingDefinition2Input
            ),
        });
        handleClose();
      }}
    >
      {formikProps => {
        const propertyFilters =
          formikProps.values.templateSearchFilterDefinitions.propertyFilters;

        const searchSortingDefinitions =
          formikProps.values.templateSearchSortingDefinitions;

        const unusedFilterProperties = differenceWith(
          templateType?.props2 ?? [],
          propertyFilters,
          (p, f) => p.key === f.key
        );

        // The rows are buffered in Formik and submitted with the rest of the
        // form, so each edit rewrites the whole array in form state.
        const setPropertyFilters = (
          next: ItemTemplateSearchFilterPropertyFilterFragment[]
        ) => {
          formikProps.setFieldValue("templateSearchFilterDefinitions", {
            propertyFilters: next,
          });
        };

        const unusedSortingProperties = differenceWith(
          templateType?.props2 ?? [],
          searchSortingDefinitions.filter(
            d => d.__typename === "EntitySearchSortingPropertyValueSorting"
          ),
          (p, f) => p.key === f.key
        ).flatMap(p => (p.__typename === "Props2Number" ? p : []));

        const templateTypePropsByKey = keyBy(
          templateType?.props2 ?? [],
          p => p.key
        );

        const replaceFilter = (
          key: string,
          next: ItemTemplateSearchFilterPropertyFilterFragment
        ) =>
          setPropertyFilters(
            propertyFilters.map(f => (f.key === key ? next : f))
          );

        const filterRows: SearchFilterOperatorRow[] = propertyFilters.map(
          propertyFilter => ({
            key: propertyFilter.key,
            label: getEnhancedTemplateSearchFilterPropLabel(propertyFilter),
            value: searchFilterValueText(propertyFilter, {
              boolLabels: {
                yes: t("Yes", { ns: "Global" }),
                no: t("No", { ns: "Global" }),
              },
            }),
            isComputed: isComputedSearchFilterSlot(propertyFilter),
            operator: searchFilterOperator(propertyFilter),
            operatorOptions: searchFilterOperatorOptions(propertyFilter),
            onChangeOperator: async operator =>
              replaceFilter(
                propertyFilter.key,
                withOperatorFragment(propertyFilter, operator)
              ),
            onDelete: async () =>
              setPropertyFilters(
                propertyFilters.filter(f => f.key !== propertyFilter.key)
              ),
          })
        );

        return (
          <Modal
            maxWidth="md"
            title={t("Configure template type: {templateName}", {
              ns: "QuoteItem",
              templateName: recommendation.teaserTitle,
            })}
            handleClose={handleClose}
            dialogProps={
              !isMaxPhone && isFullScreen ? { fullScreen: true } : undefined
            }
            headerActions={
              !isMaxPhone ? (
                <FullScreenToggleIconButton
                  isFullScreen={isFullScreen}
                  setIsFullScreen={setIsFullScreen}
                />
              ) : undefined
            }
            alwaysVisible
            actionButtons={[
              {
                label: t("Cancel", { ns: "Global" }),
                handleClick: handleClose,
                buttonProps: {
                  variant: "text",
                  disabled: formikProps.isSubmitting,
                },
              },
              {
                label: t("Remove template type", { ns: "QuoteItem" }),
                handleClick: async () => {
                  if (isDeleting) return;
                  const result = await startConfirmProcess({
                    title: t("Confirm deletion", { ns: "Global" }),
                    text: t(
                      "Are you sure you would like to delete this recommended template type?",
                      {
                        ns: "QuoteItem",
                      }
                    ),
                    confirmButtonLabel: t("Delete", { ns: "Global" }),
                  });
                  if (!result) return;
                  setIsDeleting(true);
                  try {
                    await handleComplete(null);
                  } finally {
                    setIsDeleting(false);
                  }
                  handleClose();
                },
                buttonProps: {
                  variant: "outlined",
                  disabled: formikProps.isSubmitting || isDeleting,
                },
              },
              {
                label: t("Save", { ns: "Global" }),
                buttonProps: {
                  form: formId,
                  type: "submit",
                  loading: formikProps.isSubmitting,
                  disabled: !formikProps.isValid || !formikProps.dirty,
                },
              },
            ]}
          >
            <Form id={formId}>
              <Stack direction="column" spacing={2}>
                <CollapseSection
                  title={t("Rules", { ns: "QuoteItem" })}
                  isInitiallyExpanded
                >
                  <Stack direction="column" spacing={1}>
                    <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                      capability="expressions"
                      permissionByType={{
                        QUOTE: "quote_expressions_update",
                        REQUIREMENT: "requirement_expressions_update",
                        TEMPLATE: "template_expressions_update",
                        BUILDING: "building_expressions_update",
                      }}
                      document={document}
                      otherwise={
                        <ExpressionInputWithVariables
                          label={t("Eligible if", { ns: "QuoteItem" })}
                          placeholder={t("Formula", { ns: "QuoteItem" })}
                          itemId={itemId}
                          docId={docId}
                          projectId={null}
                          inputState={formikProps.values.eligibleIfExpr}
                          disabled={true}
                          direction="row"
                          onInputStateChange={noop}
                        />
                      }
                    >
                      <ExpressionInputWithVariables
                        label={t("Eligible if", { ns: "QuoteItem" })}
                        placeholder={t("Formula", { ns: "QuoteItem" })}
                        itemId={itemId}
                        docId={docId}
                        projectId={null}
                        expressionError={
                          eligibleIfExprError
                            ? new Error(eligibleIfExprError.messageText)
                            : null
                        }
                        inputState={formikProps.values.eligibleIfExpr}
                        onInputStateChange={expr => {
                          formikProps.setFieldValue("eligibleIfExpr", expr);
                        }}
                        autoFocus={false}
                        direction="row"
                        allowedSources={EXPRESSION_SOURCES}
                        showSelectedVariableAllowedValues={true}
                      />
                    </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
                    <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                      capability="expressions"
                      permissionByType={{
                        QUOTE: "quote_expressions_update",
                        REQUIREMENT: "requirement_expressions_update",
                        TEMPLATE: "template_expressions_update",
                        BUILDING: "building_expressions_update",
                      }}
                      document={document}
                      otherwise={
                        <ExpressionInputWithVariables
                          label={t("Include if", { ns: "QuoteItem" })}
                          placeholder={t("Formula", { ns: "QuoteItem" })}
                          itemId={itemId}
                          docId={docId}
                          projectId={null}
                          inputState={formikProps.values.includeIfExpr}
                          disabled={true}
                          direction="row"
                          onInputStateChange={noop}
                        />
                      }
                    >
                      <ExpressionInputWithVariables
                        label={t("Include if", { ns: "QuoteItem" })}
                        placeholder={t("Formula", { ns: "QuoteItem" })}
                        itemId={itemId}
                        docId={docId}
                        projectId={null}
                        expressionError={
                          includeIfExprError
                            ? new Error(includeIfExprError.messageText)
                            : null
                        }
                        inputState={formikProps.values.includeIfExpr}
                        onInputStateChange={expr => {
                          formikProps.setFieldValue("includeIfExpr", expr);
                        }}
                        autoFocus={false}
                        direction="row"
                        allowedSources={EXPRESSION_SOURCES}
                        showSelectedVariableAllowedValues={true}
                      />
                    </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
                  </Stack>
                </CollapseSection>
                <CollapseSection
                  title={t("Search filters", { ns: "TemplateTypes" })}
                  itemCount={propertyFilters.length}
                  ActionButtons={
                    /* Adding a filter creates it authored, which needs no
                       expression access — only the formula affordances on the
                       rows are gated. */
                    <Stack direction={"row"} spacing={1}>
                      {/* Disabled rather than unmounted while empty: the button
                          owns the open modal, which must survive deleting the
                          last row. */}
                      <ModalOpenButton
                        Modal={SearchFilterOperatorsModal}
                        modalProps={{
                          rows: filterRows,
                          renderExpressionGate,
                        }}
                      >
                        <IconButton
                          color={"primary"}
                          size={"small"}
                          disabled={propertyFilters.length === 0}
                          aria-label={t("Edit search filters", {
                            ns: "QuoteItem",
                          })}
                        >
                          <EditIcon />
                        </IconButton>
                      </ModalOpenButton>
                      <MenuButton
                        Icon={<AddIcon />}
                        buttonProps={{
                          disabled: unusedFilterProperties.length === 0,
                          "aria-label": t("Add search filter", {
                            ns: "QuoteItem",
                          }),
                        }}
                      >
                        {unusedFilterProperties.map(prop => (
                          <MenuItem
                            key={prop.key}
                            onClick={() => {
                              setPropertyFilters([
                                ...propertyFilters,
                                newAuthoredFilterForProp(prop),
                              ]);
                            }}
                          >
                            {getEnhancedPropertyLabel(prop, false)}
                          </MenuItem>
                        ))}
                      </MenuButton>
                    </Stack>
                  }
                >
                  <Grid
                    container
                    columns={2}
                    rowSpacing={1}
                    columnSpacing={2}
                    sx={{ padding: 1 }}
                  >
                    {propertyFilters.map(propertyFilter => (
                      <Grid key={propertyFilter.key} size={1}>
                        <SearchFilterField
                          projectId={null}
                          docId={docId}
                          itemId={itemId}
                          label={getEnhancedTemplateSearchFilterPropLabel(
                            propertyFilter
                          )}
                          {...searchFilterFieldPropsForSlot(propertyFilter)}
                          valueInput={
                            <SearchFilterValueInput
                              slot={propertyFilter}
                              label={getEnhancedTemplateSearchFilterPropLabel(
                                propertyFilter
                              )}
                              valueOptions={searchFilterValueOptionsFromProps2(
                                templateTypePropsByKey[propertyFilter.key]
                              )}
                              buildEntry={authoredFilterFragment}
                              onChange={async entry =>
                                replaceFilter(propertyFilter.key, entry)
                              }
                            />
                          }
                          onSaveExpression={async (operator, expr) => {
                            setPropertyFilters(
                              propertyFilters.map(f =>
                                f.key === propertyFilter.key
                                  ? withExpressionFragment(f, operator, expr)
                                  : f
                              )
                            );
                          }}
                          onRemoveExpression={async () => {
                            setPropertyFilters(
                              propertyFilters.map(f =>
                                f.key === propertyFilter.key
                                  ? withoutExpressionFragment(f)
                                  : f
                              )
                            );
                          }}
                          onDeleteFilter={async () => {
                            setPropertyFilters(
                              propertyFilters.filter(
                                f => f.key !== propertyFilter.key
                              )
                            );
                          }}
                          renderExpressionGate={renderExpressionGate}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </CollapseSection>
                <CollapseSection
                  title={t("Sorting", { ns: "TemplateTypes" })}
                  ActionButtons={
                    <>
                      <MenuButton
                        Icon={<AddIcon />}
                        buttonProps={{
                          disabled: unusedSortingProperties.length === 0,
                        }}
                      >
                        {unusedSortingProperties.map(prop => {
                          return (
                            <MenuItem
                              onClick={() => {
                                formikProps.setValues(values => ({
                                  ...values,
                                  templateSearchSortingDefinitions:
                                    values.templateSearchSortingDefinitions.concat(
                                      prop.__typename === "Props2Number"
                                        ? {
                                            __typename:
                                              "EntitySearchSortingPropertyValueSorting",
                                            key: prop.key,
                                            kind: "number",
                                            direction: "asc",
                                          }
                                        : assertNever(prop.__typename)
                                    ),
                                }));
                              }}
                            >
                              {prop.key}
                            </MenuItem>
                          );
                        })}
                      </MenuButton>
                      <MenuButton>
                        <ViewModeMenuItem
                          allowedModes={[null, "delete"]}
                          viewMode={viewModeSearchSortings}
                          onViewModeChange={setViewModeSearchSortings}
                        />
                      </MenuButton>
                    </>
                  }
                >
                  <Grid
                    container
                    columns={2}
                    rowSpacing={1}
                    columnSpacing={2}
                    sx={{
                      padding: 1,
                    }}
                  >
                    {searchSortingDefinitions.map((searchSorting, index) => {
                      switch (searchSorting.__typename) {
                        case "EntitySearchSortingPropertyValueSorting": {
                          return (
                            <Grid key={searchSorting.key} size={1}>
                              <Stack
                                direction={"row"}
                                spacing={0.5}
                                sx={{
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Select
                                  key={`${searchSorting.key}-input`}
                                  label={searchSorting.key}
                                  options={[
                                    {
                                      label: "asc",
                                      value: "asc" as const,
                                    },
                                    {
                                      label: "desc",
                                      value: "desc" as const,
                                    },
                                  ]}
                                  value={searchSorting.direction}
                                  onChange={value => {
                                    formikProps.setValues(values => ({
                                      ...values,
                                      templateSearchSortingDefinitions:
                                        values.templateSearchSortingDefinitions.map(
                                          d =>
                                            d.__typename ===
                                              searchSorting.__typename &&
                                            d.key === searchSorting.key
                                              ? { ...d, direction: value }
                                              : d
                                        ),
                                    }));
                                  }}
                                />
                                {viewModeSearchSortings === "delete" && (
                                  <IconButton
                                    color="primary"
                                    size="small"
                                    onClick={() => {
                                      formikProps.setValues(values => ({
                                        ...values,
                                        templateSearchSortingDefinitions:
                                          values.templateSearchSortingDefinitions.filter(
                                            d =>
                                              !(
                                                d.__typename ===
                                                  searchSorting.__typename &&
                                                d.key === searchSorting.key
                                              )
                                          ),
                                      }));
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                )}
                              </Stack>
                            </Grid>
                          );
                        }
                        default:
                          assertNever(searchSorting.__typename);
                      }
                    })}
                  </Grid>
                </CollapseSection>
              </Stack>
            </Form>
            <ConfirmProcess ref={confirmProcessRef} />
          </Modal>
        );
      }}
    </Formik>
  );
};
